import { supabase } from "@/integrations/supabase/client";

const VIEWED_POSTS_STORAGE_KEY = "mnre_viewed_blog_posts";
const VIEW_COOLDOWN_MS = 60 * 60 * 1000; // 1-hour cooldown per browser session to prevent reload spam

/**
 * Checks whether this article view should be counted to prevent artificial inflation from page refreshes.
 */
function shouldCountView(slug: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = localStorage.getItem(VIEWED_POSTS_STORAGE_KEY);
    const viewedMap: Record<string, number> = raw ? JSON.parse(raw) : {};
    const now = Date.now();
    const lastViewed = viewedMap[slug] || 0;

    if (now - lastViewed < VIEW_COOLDOWN_MS) {
      return false; // Already counted recently
    }

    viewedMap[slug] = now;
    localStorage.setItem(VIEWED_POSTS_STORAGE_KEY, JSON.stringify(viewedMap));
    return true;
  } catch {
    return true;
  }
}

/**
 * Records an authentic view for a blog article in the backend.
 * Increments views_count in PostgreSQL and returns the updated count.
 */
export async function trackBlogArticleView(slug: string): Promise<number | null> {
  if (!slug) return null;
  const cleanSlug = slug.toLowerCase().trim();

  if (!shouldCountView(cleanSlug)) {
    return null;
  }

  try {
    // 1. Invoke Supabase Edge Function
    const { data: edgeData, error: edgeErr } = await supabase.functions.invoke(
      "track-blog-view",
      { body: { slug: cleanSlug } }
    );

    if (!edgeErr) {
      return typeof edgeData?.views_count === "number" ? edgeData.views_count : null;
    }

    // 2. Fallback to PostgreSQL RPC function
    const { data: rpcData, error: rpcErr } = await supabase.rpc(
      "record_blog_post_view" as any,
      { p_slug: cleanSlug }
    );

    if (!rpcErr && typeof rpcData === "number") {
      return rpcData;
    }
  } catch (err) {
    console.warn("[trackBlogArticleView] Backend view tracking error:", err);
  }

  return null;
}

